import { useEffect } from 'react'

import { consumeFreshLogin, clearFreshLogin } from './fresh-login'
import { postDemoEmployeeLogin } from './demo-event.api'

const DEMO_NAME_PREFIX = 'ZZ - DEMO - '

/**
 * Fires the `employee_login` EPS demo event exactly once per real login for
 * an employee of a demo company.
 *
 * Accepts `clientPublicKey` and `companyName` as arguments rather than
 * reading from the Redux store directly, keeping this hook compatible with
 * react-redux v5 (which has no `useSelector`). Thread them from
 * `EnrollmeLayoutContainer`'s `mapStateToProps`.
 *
 * Conditions that must all be true before the event is sent:
 * - The current session started from a real username/password or MFA login
 *   (not a page reload or silent token refresh).
 * - Both `clientPublicKey` and `companyName` have resolved (loaded after
 *   bridge-login and user-session bootstrap).
 * - `companyName` starts with "ZZ - DEMO - " (demo company signal).
 *
 * When the conditions are not met the fresh-login flag is cleared so a later
 * company switch cannot accidentally send the event.
 *
 * Call this hook inside EnrollmeLayout, analogous to how useDemoLoginEvent
 * works in admin-web-client's AdminLayout.
 *
 * @param {string|undefined} clientPublicKey - From userSession selectors
 * @param {string|undefined} companyName     - From userSession selectors
 */
export function useDemoLoginEvent(clientPublicKey, companyName) {
  const isDemoEmployee = Boolean(companyName && companyName.startsWith(DEMO_NAME_PREFIX))

  useEffect(() => {
    // Wait until both values have loaded from the session.
    if (!clientPublicKey || !companyName) return

    if (isDemoEmployee && consumeFreshLogin()) {
      postDemoEmployeeLogin(clientPublicKey)
    } else {
      // Not a demo employee (or flag already consumed) — discard any pending
      // flag so it doesn't linger if the user later switches employer.
      clearFreshLogin()
    }
  }, [clientPublicKey, companyName, isDemoEmployee])
}
